import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { TePreviewLinksPanel } from "./TePreviewLinksPanel";
import {
  Package, Download, ExternalLink, RefreshCw, Copy, CheckCircle2,
  Volume2, BookOpen, Globe, Users, FileText, Loader2,
  Fingerprint, Calendar, HardDrive, AlertCircle, LineChart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Small UI atoms ───────────────────────────────────────────────

const Stat = ({ icon: Icon, value, label, color = "#334155" }) => (
  <div className="flex items-center gap-2 min-w-0">
    <div
      className="w-7 h-7 rounded flex items-center justify-center shrink-0"
      style={{ backgroundColor: `${color}18` }}
    >
      <Icon size={14} style={{ color }} />
    </div>
    <div className="min-w-0">
      <div className="text-sm font-bold leading-tight truncate">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 truncate">
        {label}
      </div>
    </div>
  </div>
);

const CopyChip = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };
  return (
    <button
      onClick={onCopy}
      data-testid={`copy-${label}-btn`}
      className="group flex items-center gap-1.5 px-2 py-1 rounded border
                 border-slate-200 bg-slate-50 hover:bg-slate-100
                 text-[11px] font-mono text-slate-600 transition-colors"
      title={text}
    >
      {copied ? (
        <CheckCircle2 size={12} className="text-emerald-600" />
      ) : (
        <Copy size={12} className="text-slate-400 group-hover:text-slate-600" />
      )}
      <span className="truncate max-w-[220px]">
        {copied ? "copied" : text}
      </span>
    </button>
  );
};

const BAND_ACCENTS = {
  K:   "#F4B13D",
  G1:  "#D96A3B",
  G2:  "#C13584",
  "34":"#4A86E8",
  "56":"#2A8C6E",
  "78":"#6B4FD9",
  "912":"#0F1E3A",
};

// ─── Band card ────────────────────────────────────────────────────

const BandCard = ({ band, token }) => {
  const f = band.features || {};
  const [preparing, setPreparing] = useState(false);
  const [prepError, setPrepError] = useState(null);
  const accent = BAND_ACCENTS[band.band_token] || "#334155";
  const sizeLabel = `${band.size_mb} MB`;
  const navigate = useNavigate();

  const launchPreview = async () => {
    setPreparing(true);
    setPrepError(null);
    // Open the window SYNCHRONOUSLY inside the click handler so Safari
    // and strict popup blockers treat it as a user-initiated navigation.
    // We land on about:blank and swap in the real URL once the backend
    // confirms the preview has been extracted.
    const w = window.open("about:blank", "_blank", "noopener,noreferrer");
    try {
      const res = await axios.post(
        `${API}/api/sample-demos/${band.serial}/preview/prepare`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const url = `${API}${res.data.entry_url}`;
      if (w) {
        w.location.href = url;
      } else {
        // Popup blocked outright — fall back to current tab.
        window.location.href = url;
      }
    } catch (err) {
      if (w) w.close();
      setPrepError(
        err?.response?.data?.detail || err.message || "Preview failed",
      );
    } finally {
      setPreparing(false);
    }
  };

  const downloadTarball = () => {
    // Direct GET with Authorization header can't be done from an <a>,
    // so we fetch as blob and trigger a download. Keeps the request
    // authenticated end-to-end.
    (async () => {
      try {
        const res = await fetch(
          `${API}/api/sample-demos/${band.serial}/download`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = band.tarball_name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        alert(`Download failed: ${err.message}`);
      }
    })();
  };

  return (
    <div
      data-testid={`sample-demo-card-${band.band_token}`}
      className="bg-white border border-slate-200 rounded-lg overflow-hidden
                 flex flex-col shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Band header bar */}
      <div
        className="h-1.5 w-full"
        style={{ backgroundColor: accent }}
      />
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div
              className="text-[10px] font-bold uppercase tracking-widest"
              style={{ color: accent }}
            >
              Sample USB · Band {band.band_label}
            </div>
            <h3 className="text-lg font-bold leading-tight mt-0.5">
              {band.band_long}
            </h3>
            <div className="text-xs text-slate-500 mt-0.5">
              Grades {band.grades}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">Size</div>
            <div className="text-sm font-semibold">{sizeLabel}</div>
          </div>
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-2 gap-2 py-2 border-t border-b border-slate-100">
          <Stat
            icon={Users}
            value={f.total_demo_students ?? 0}
            label="Demo students"
            color="#22c55e"
          />
          <Stat
            icon={FileText}
            value={f.scenarios_count ?? 0}
            label="Demo scenarios"
            color="#3b82f6"
          />
          <Stat
            icon={Volume2}
            value={f.audio_clips ?? 0}
            label="Audio clips"
            color="#f59e0b"
          />
          <Stat
            icon={BookOpen}
            value={f.booklets ?? 0}
            label="Booklets (PDF)"
            color="#6366f1"
          />
          <Stat
            icon={Globe}
            value={f.parent_letter_language_count ?? 0}
            label="Parent-letter langs"
            color="#06b6d4"
          />
          <Stat
            icon={Calendar}
            value={f.pacing_weeks_pdf ?? 0}
            label="Pacing PDFs"
            color="#8b5cf6"
          />
        </div>

        {/* Scenario breakdown */}
        {Array.isArray(f.scenarios) && f.scenarios.length > 0 && (
          <div className="text-[11px]">
            <div className="font-semibold text-slate-700 mb-1">
              QC rosters bundled:
            </div>
            <ul className="space-y-0.5 text-slate-600">
              {f.scenarios.map((s) => (
                <li key={s.filename} className="flex justify-between">
                  <span className="font-mono text-[10px] truncate">
                    {s.scenario_id || s.filename}
                  </span>
                  <span className="shrink-0">
                    {s.students} students
                    {s.grade_band ? ` · ${s.grade_band}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Parent-letter languages */}
        {Array.isArray(f.parent_letter_languages)
          && f.parent_letter_languages.length > 0 && (
          <div className="text-[11px]">
            <div className="font-semibold text-slate-700 mb-1">
              Parent-letter languages ({f.family_practice_sheets_total || 0} sheets):
            </div>
            <div className="flex flex-wrap gap-1">
              {f.parent_letter_languages.map((lang) => (
                <span
                  key={lang}
                  className="px-1.5 py-0.5 bg-slate-100 border border-slate-200
                             rounded text-[10px] font-mono uppercase"
                >
                  {lang}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* SHA-256 */}
        <div className="text-[11px]">
          <div className="font-semibold text-slate-700 mb-1 flex items-center gap-1">
            <Fingerprint size={11} /> SHA-256
          </div>
          <CopyChip text={band.sha256 || "—"} label={`sha-${band.band_token}`} />
        </div>

        {/* Serial + build date */}
        <div className="text-[10px] text-slate-400 font-mono">
          {band.serial} · built {band.mtime_iso}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-1 mt-auto">
          <Button
            size="sm"
            className="flex-1 bg-slate-900 hover:bg-slate-800 text-white btn-sharp"
            onClick={downloadTarball}
            data-testid={`sample-demo-download-${band.band_token}`}
          >
            <Download size={13} className="mr-1.5" />
            Download
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 btn-sharp border-slate-300"
            onClick={launchPreview}
            disabled={preparing}
            data-testid={`sample-demo-preview-${band.band_token}`}
          >
            {preparing ? (
              <><Loader2 size={13} className="mr-1.5 animate-spin" /> Preparing…</>
            ) : (
              <><ExternalLink size={13} className="mr-1.5" /> Launch Preview</>
            )}
          </Button>
        </div>

        {/* v6.33.9 — Class Data preview (deterministic 15-student roster
            + growth charts + ILP drafts). Opens in-app, no USB flash. */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 btn-sharp border-sky-300 text-sky-700 hover:bg-sky-50"
            onClick={() => navigate(`/superadmin/sample-demo-class/${encodeURIComponent(band.band_token)}`)}
            data-testid={`sample-demo-class-data-${band.band_token}`}
          >
            <LineChart size={13} className="mr-1.5" />
            View Class Data &amp; Reports
          </Button>
        </div>

        {prepError && (
          <div className="text-[11px] text-rose-600 flex items-start gap-1 mt-1">
            <AlertCircle size={12} className="shrink-0 mt-0.5" />
            <span>{prepError}</span>
          </div>
        )}
      </div>
    </div>
  );
};


// ─── Main tab ─────────────────────────────────────────────────────

const SampleDemosTab = () => {
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState("");

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // SuperAdminDashboard stores the session token under
      // `mosaic_session_token` (AuthContext.TOKEN_KEY). Keep a few
      // historical fallbacks so older browser sessions still work.
      const t =
        localStorage.getItem("mosaic_session_token") ||
        localStorage.getItem("session_token") ||
        localStorage.getItem("sessionToken") ||
        localStorage.getItem("token") ||
        "";
      setToken(t);
      const res = await axios.get(`${API}/api/sample-demos/`, {
        headers: t ? { Authorization: `Bearer ${t}` } : undefined,
      });
      setBands(res.data.bands || []);
    } catch (err) {
      setError(
        err?.response?.data?.detail || err.message || "Failed to load demos",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const totalSize = bands.reduce((a, b) => a + (b.size_bytes || 0), 0);
  const totalStudents = bands.reduce(
    (a, b) => a + (b.features?.total_demo_students || 0), 0,
  );
  // Derive the parent-letter language count from whichever band ships
  // the most languages (same bundle across all bands today, but we read
  // it from the data so adding a new language only requires rebuilding
  // the tarballs — no UI change).
  const parentLangCount = bands.reduce(
    (a, b) => Math.max(a, b.features?.parent_letter_language_count || 0), 0,
  );
  // Max roster + pacing counts (same across bands today) for the header.
  const maxRosters = bands.reduce(
    (a, b) => Math.max(a, b.features?.scenarios_count || 0), 0,
  );
  const maxPacing = bands.reduce(
    (a, b) => Math.max(a, b.features?.pacing_weeks_pdf || 0), 0,
  );
  const avgStudentsPerBand = bands.length
    ? Math.round(totalStudents / bands.length)
    : 0;

  return (
    <div className="space-y-5" data-testid="sample-demos-root">
      <TePreviewLinksPanel />
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-indigo-50 flex items-center justify-center">
            <Package size={20} className="text-indigo-600" />
          </div>
          <div>
            <h2
              className="text-xl font-bold"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Sample Demo USBs
            </h2>
            <p className="text-sm text-slate-500 max-w-xl">
              Browse, preview and download every sample MOSAIC USB currently
              in <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">/app/usb_images/</code>.
              Each sample ships a full offline Teacher Portal with
              {avgStudentsPerBand > 0 ? ` ~${avgStudentsPerBand} demo students across ${maxRosters} QC rosters, ` : " QC rosters, "}
              audio clips, parent letters in
              {parentLangCount > 0 ? ` ${parentLangCount} NYC languages, ` : " NYC languages, "}
              assessment booklets and a {maxPacing > 0 ? `${maxPacing}-PDF ` : ""}
              pacing guide. Click <em>Launch Preview</em> to walk through the
              real Portal without flashing a USB.
            </p>
          </div>
        </div>
        <Button
          onClick={fetchList}
          variant="outline"
          size="sm"
          className="btn-sharp"
          data-testid="sample-demos-refresh"
        >
          <RefreshCw size={14} className="mr-1.5" />
          Refresh
        </Button>
      </div>

      {/* Summary strip */}
      {!loading && bands.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
          <Stat
            icon={Package}
            value={bands.length}
            label="Bands available"
            color="#6366f1"
          />
          <Stat
            icon={Users}
            value={totalStudents}
            label="Total demo students"
            color="#22c55e"
          />
          <Stat
            icon={HardDrive}
            value={`${(totalSize / (1024 * 1024 * 1024)).toFixed(2)} GB`}
            label="Combined size"
            color="#f59e0b"
          />
          <Stat
            icon={Globe}
            value={parentLangCount}
            label="NYC parent langs"
            color="#06b6d4"
          />
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          className="bg-rose-50 border border-rose-200 text-rose-700 text-sm
                     p-3 rounded flex items-start gap-2"
          data-testid="sample-demos-error"
        >
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div
          className="text-sm text-slate-500 flex items-center gap-2"
          data-testid="sample-demos-loading"
        >
          <Loader2 size={14} className="animate-spin" />
          Scanning /app/usb_images/…
        </div>
      )}

      {/* Grid */}
      {!loading && bands.length > 0 && (
        <div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
          data-testid="sample-demos-grid"
        >
          {bands.map((b) => (
            <BandCard key={b.serial} band={b} token={token} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && bands.length === 0 && (
        <div
          className="text-sm text-slate-500 bg-slate-50 border border-slate-200
                     rounded p-6 text-center"
          data-testid="sample-demos-empty"
        >
          No sample USBs found. Drop a{" "}
          <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border">
            MOS-USB-&lt;BAND&gt;-&lt;DATE&gt;.tar.gz
          </code>{" "}
          into <code className="font-mono text-xs">/app/usb_images/</code>{" "}
          and refresh this tab.
        </div>
      )}
    </div>
  );
};

export default SampleDemosTab;
